import React, { useEffect, useRef, useState } from 'react';

import log from 'loglevel';
import { Check, ChevronDown } from 'lucide-react';

import { useTenant } from '../../contexts/TenantContext';

import './TenantSwitcher.css';

interface TenantSwitcherProps {
  /**
   * `default` – full-width button used inside the sidebar header.
   * `sidebar` – foot of the dark rail, menu opens upward.
   * `chip` – compact pill used by TenantEcho in the page header.
   */
  variant?: 'default' | 'chip' | 'sidebar';
}

function getTenantLogoUrl(tenant: { invoiceHeader?: { logoUrl?: string | null } | null } | null | undefined): string | null {
  return tenant?.invoiceHeader?.logoUrl ?? null;
}

interface TenantAvatarProps {
  logoUrl: string | null;
  name: string;
  /** Class applied to the `<img>`; the initial fallback has its own class. */
  imageClassName: string;
}

/**
 * Tenant logo with a first-letter fallback.
 *
 * A logo URL that fails to load (an expired presigned link, a deleted object)
 * would otherwise render as the browser's broken-image outline — an empty box
 * where the tenant identity should be. Falling back to the initial on `error`
 * means the switcher always shows *something* identifiable.
 */
const TenantAvatar: React.FC<TenantAvatarProps> = ({ logoUrl, name, imageClassName }) => {
  const [failed, setFailed] = useState(false);

  // A new URL (re-signed, or a freshly uploaded logo) deserves another attempt.
  useEffect(() => {
    setFailed(false);
  }, [logoUrl]);

  if (!logoUrl || failed) {
    return (
      <span className="tenant-initial" aria-hidden="true">
        {name.charAt(0)}
      </span>
    );
  }

  return (
    <img
      src={logoUrl}
      alt=""
      className={imageClassName}
      aria-hidden="true"
      onError={() => setFailed(true)}
    />
  );
};

export const TenantSwitcher: React.FC<TenantSwitcherProps> = ({ variant = 'default' }) => {
  const { selectedTenant, availableTenants, switchTenant } = useTenant();
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSwitch = async (tenantId: string) => {
    if (isSwitching || tenantId === selectedTenant?.id) return;

    try {
      setIsSwitching(true);
      setIsOpen(false);
      await switchTenant(tenantId);
    } catch (error) {
      log.error('Failed to switch tenant:', error);
      setIsSwitching(false);
    }
  };

  if (!selectedTenant || availableTenants.length <= 1) {
    return null;
  }

  const selectedLogo = getTenantLogoUrl(selectedTenant as { invoiceHeader?: { logoUrl?: string | null } | null });

  return (
    <div className={`tenant-switcher tenant-switcher--${variant}`} ref={dropdownRef}>
      <button
        type="button"
        className={`tenant-trigger tenant-trigger--${variant} ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isSwitching}
        aria-label={`Switch tenant (currently ${selectedTenant.name})`}
      >
        <TenantAvatar
          logoUrl={selectedLogo}
          name={selectedTenant.name}
          imageClassName="tenant-trigger-logo"
        />
        <span className="tenant-trigger-name">{selectedTenant.name}</span>
        <ChevronDown className={`chevron ${isOpen ? 'rotated' : ''}`} />
      </button>

      {isOpen && (
        <div className="tenant-dropdown">
          {availableTenants.map((userTenant) => {
            const isSelected = selectedTenant.id === userTenant.tenantId;
            const logo = getTenantLogoUrl(userTenant.tenant as { invoiceHeader?: { logoUrl?: string | null } | null });

            return (
              <button
                type="button"
                key={userTenant.tenantId}
                className={`dropdown-item ${isSelected ? 'active' : ''}`}
                onClick={() => handleSwitch(userTenant.tenantId)}
                disabled={isSelected || isSwitching}
              >
                <TenantAvatar
                  logoUrl={logo}
                  name={userTenant.tenant.name}
                  imageClassName="dropdown-item-logo"
                />
                <span className="dropdown-item-name">{userTenant.tenant.name}</span>
                {isSelected && <Check className="check-icon" />}
              </button>
            );
          })}
        </div>
      )}

      {isSwitching && (
        <div className="switching-overlay">
          <div className="switching-message">
            <div className="spinner" />
            <span>Switching tenant...</span>
          </div>
        </div>
      )}
    </div>
  );
};
